import { CNS_API_BASE } from "@/config";
import { CNSLearningAnnotation, JsonPatchOperation } from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";
import { generateLearningAnnotationId } from "@/utils/ksuidGenerator";

const LEARNING_ANNOTATIONS_URL = `${CNS_API_BASE}/learning-annotations`;

/**
 * GET `.../projects/{projectId}/learnings/{learningId}/learning-annotations`.
 *
 * @param projectId - Project public ID
 * @param learningId - Learning public ID
 * @returns Parsed `LearningAnnotation`, or `null` if the response status is 404.
 * @throws If the response is not OK and not 404; delegates to `throwOnProblemResponse`.
 */
export async function fetchLearningAnnotationForLearning(
  projectId: string,
  learningId: string
): Promise<CNSLearningAnnotation | null> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/learnings/${learningId}/learning-annotations`,
    { credentials: "include" }
  );
  if (res.status === 404) return null;
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch learning annotation");
  return res.json();
}

/**
 * PATCH `.../projects/{projectId}/learning-annotations/{annotationId}` (JSON Patch).
 *
 * @param projectId - Project public ID
 * @param annotationId - Annotation public ID
 * @param patchOperations - RFC 6902 operations
 * @returns Updated annotation from the response body
 */
export async function updateLearningAnnotation(
  projectId: string,
  annotationId: string,
  patchOperations: JsonPatchOperation[]
): Promise<CNSLearningAnnotation> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/learning-annotations/${annotationId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(patchOperations),
    }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to update learning annotation");
  return res.json();
}

/**
 * POST `/learning-annotations` with a client-generated annotation public ID.
 *
 * @param learningId - Target learning public ID
 * @param annotation - Optional summary, tags, and notes
 * @returns Created annotation from the response body
 */
export async function createLearningAnnotation(
  learningId: string,
  annotation: Partial<CNSLearningAnnotation>
): Promise<CNSLearningAnnotation> {
  const annotationId = generateLearningAnnotationId();

  const res = await fetch(LEARNING_ANNOTATIONS_URL, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: annotationId,
      learningId: learningId,
      summary: annotation.summary || "",
      tags: annotation.tags || [],
      notes: annotation.notes || "",
    }),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to create learning annotation");
  return res.json();
}

